import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { database, db } from '../firebase';
import { useShadowMode } from '../contexts/ShadowModeContext';

const TABS = ['Perfil', 'Licencias', 'Dispositivos', 'Versiones', 'Quizzes', 'Raw'];

const AdminShadowPanel = () => {
  const navigate = useNavigate();
  const { activateShadow, isShadowActive, shadowUid, deactivateShadow } = useShadowMode();
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [foundUsers, setFoundUsers] = useState([]);
  const [selectedUid, setSelectedUid] = useState(null);
  const [activeTab, setActiveTab] = useState('Perfil');
  const [userData, setUserData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  const normalizeEmail = (email) => email?.replace(/\./g, '_dot_').replace(/@/g, '_at_') || '';

  const searchUsers = useCallback(async () => {
    const term = searchTerm.trim();
    if (!term) return;
    setSearching(true);
    setError('');
    setFoundUsers([]);
    setSelectedUid(null);
    setUserData(null);

    try {
      const results = [];

      // 1. Buscar por UID exacto en RTDB licencesPremium
      const licPremiumSnapshot = await get(ref(database, 'licencesPremium'));
      if (licPremiumSnapshot.exists()) {
        const data = licPremiumSnapshot.val();
        for (const [key, val] of Object.entries(data)) {
          const email = (val.email || '').toLowerCase();
          const uid = (val.uid || '').toLowerCase();
          if (uid === term.toLowerCase() || email.includes(term.toLowerCase())) {
            results.push({ uid: val.uid || key, email: val.email || key, source: 'licencesPremium', key });
          }
        }
      }

      // 2. Buscar en licenses (RTDB)
      const licSnapshot = await get(ref(database, 'licenses'));
      if (licSnapshot.exists()) {
        const data = licSnapshot.val();
        for (const [key, val] of Object.entries(data)) {
          const email = (val.email || '').toLowerCase();
          const uid = (val.uid || '').toLowerCase();
          if (uid === term.toLowerCase() || email.includes(term.toLowerCase())) {
            if (!results.some(r => r.uid === (val.uid || key))) {
              results.push({ uid: val.uid || key, email: val.email || key, source: 'licenses', key });
            }
          }
        }
      }

      // 3. Buscar en usersweb (RTDB)
      const usersWebSnapshot = await get(ref(database, 'usersweb'));
      if (usersWebSnapshot.exists()) {
        const data = usersWebSnapshot.val();
        for (const [uid, val] of Object.entries(data)) {
          const email = (val.email || '').toLowerCase();
          const displayName = (val.displayName || '').toLowerCase();
          if (uid === term.toLowerCase() || email.includes(term.toLowerCase()) || displayName.includes(term.toLowerCase())) {
            if (!results.some(r => r.uid === uid)) {
              results.push({ uid, email: val.email || uid, displayName: val.displayName, source: 'usersweb' });
            }
          }
        }
      }

      // 4. Buscar en Firestore users collection
      if (results.length === 0 || term.includes('@')) {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', term.toLowerCase()));
        const snap = await getDocs(q);
        snap.forEach(d => {
          const data = d.data();
          if (!results.some(r => r.uid === d.id)) {
            results.push({ uid: d.id, email: data.email || '', displayName: data.displayName || data.nick, source: 'firestore' });
          }
        });
      }

      if (results.length === 0) {
        setError('No se encontraron usuarios con ese criterio.');
      } else {
        // Deduplicar por UID
        const seen = new Set();
        const unique = results.filter(r => {
          if (seen.has(r.uid)) return false;
          seen.add(r.uid);
          return true;
        });
        setFoundUsers(unique);
        if (unique.length === 1) {
          loadUserData(unique[0].uid);
        }
      }
    } catch (err) {
      setError('Error en búsqueda: ' + err.message);
    } finally {
      setSearching(false);
    }
  }, [searchTerm]);

  const loadUserData = async (uid) => {
    setSelectedUid(uid);
    setLoadingData(true);
    setError('');

    try {
      const data = { uid };

      // Firestore profile
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) data.profile = userDoc.data();

      // RTDB userVersions
      const verSnap = await get(ref(database, `userVersions/${uid}`));
      if (verSnap.exists()) data.versions = verSnap.val();

      // RTDB usersweb
      const webSnap = await get(ref(database, `usersweb/${uid}`));
      if (webSnap.exists()) data.usersweb = webSnap.val();

      // RTDB devices (buscar por userId)
      const devicesSnap = await get(ref(database, 'devices'));
      if (devicesSnap.exists()) {
        const allDevices = devicesSnap.val();
        data.devices = Object.entries(allDevices)
          .filter(([, v]) => v.userId === uid || v.userEmail?.toLowerCase().includes(uid.toLowerCase()))
          .map(([k, v]) => ({ id: k, ...v }));
      }

      // Licencias: UID es la única clave canónica.
      const licPremiumSnap = await get(ref(database, `licencesPremium/${uid}`));
      if (licPremiumSnap.exists()) data.licencePremium = licPremiumSnap.val();
      const licSnap = await get(ref(database, `licenses/${uid}`));
      if (licSnap.exists()) data.license = licSnap.val();

      // Quizzes (Firestore)
      try {
        const quizzesSnap = await getDocs(collection(db, 'user_quizzes', uid, 'quizzes'));
        data.quizzes = quizzesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) {
        data.quizzes = [];
      }

      setUserData(data);
    } catch (err) {
      setError('Error cargando datos: ' + err.message);
    } finally {
      setLoadingData(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') searchUsers();
  };

  const formatDate = (ts) => {
    if (!ts) return 'N/A';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return String(ts); }
  };

  const renderProfileTab = () => {
    const p = userData?.profile;
    if (!p) return <div className="sp-empty">Sin perfil en Firestore</div>;
    return (
      <div className="sp-profile-grid">
        <div className="sp-field"><label>Email</label><span>{p.email || p.correo || '—'}</span></div>
        <div className="sp-field"><label>Nombre</label><span>{p.displayName || p.nick || '—'}</span></div>
        <div className="sp-field"><label>Institución</label><span>{p.institucion || '—'}</span></div>
        <div className="sp-field"><label>Jerarquía</label><span>{p.grado || p.jerarquia || '—'}</span></div>
        <div className="sp-field"><label>Cuerpo/Servicio</label><span>{p.cuerpoServicio || p.cuerpo || '—'}</span></div>
        <div className="sp-field"><label>Especialidad</label><span>{p.especialidad || '—'}</span></div>
        <div className="sp-field"><label>Premium</label><span>{p.isPremium ? '✅ Sí' : '❌ No'}</span></div>
        <div className="sp-field"><label>Premium Fin</label><span>{formatDate(p.premiumEndDate)}</span></div>
        <div className="sp-field"><label>UID</label><span style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>{selectedUid}</span></div>
      </div>
    );
  };

  const renderLicensesTab = () => {
    const lp = userData?.licencePremium;
    const lic = userData?.license;
    if (!lp && !lic) return <div className="sp-empty">Sin licencias registradas</div>;
    return (
      <div className="sp-licenses">
        {lp && (
          <div className="sp-license-card">
            <h4>licencesPremium {lp.isActive !== false && lp.isPremium !== false ? '✅' : '❌'}</h4>
            <div className="sp-field"><label>Email</label><span>{lp.email}</span></div>
            <div className="sp-field"><label>Tipo</label><span>{lp.licenseType || lp.isTrial ? 'Trial' : 'Premium'}</span></div>
            <div className="sp-field"><label>Activo</label><span>{lp.isActive !== false ? 'Sí' : 'No'}</span></div>
            <div className="sp-field"><label>Premium</label><span>{lp.isPremium !== false ? 'Sí' : 'No'}</span></div>
            <div className="sp-field"><label>Vence</label><span>{formatDate(lp.premiumEndDate || lp.expirationDate)}</span></div>
            <div className="sp-field"><label>Ult. Activación</label><span>{formatDate(lp.lastActivationDate)}</span></div>
          </div>
        )}
        {lic && (
          <div className="sp-license-card">
            <h4>licenses {lic.isPremium ? '✅' : '❌'}</h4>
            <div className="sp-field"><label>Email</label><span>{lic.email}</span></div>
            <div className="sp-field"><label>Premium</label><span>{lic.isPremium ? 'Sí' : 'No'}</span></div>
            <div className="sp-field"><label>Activo</label><span>{lic.isActive !== false ? 'Sí' : 'No'}</span></div>
            <div className="sp-field"><label>Vence</label><span>{formatDate(lic.premiumEndDate || lic.expirationDate)}</span></div>
          </div>
        )}
      </div>
    );
  };

  const renderDevicesTab = () => {
    const devices = userData?.devices;
    if (!devices || devices.length === 0) return <div className="sp-empty">Sin dispositivos registrados</div>;
    return (
      <div className="sp-devices">
        {devices.map((d, i) => (
          <div key={d.id || i} className="sp-device-card">
            <div className="sp-field"><label>ID</label><span style={{ fontSize: '0.7rem' }}>{d.id}</span></div>
            {d.deviceInfo?.manufacturer && <div className="sp-field"><label>Fabricante</label><span>{d.deviceInfo.manufacturer}</span></div>}
            {d.deviceInfo?.model && <div className="sp-field"><label>Modelo</label><span>{d.deviceInfo.model}</span></div>}
            {d.deviceInfo?.androidVersion && <div className="sp-field"><label>Android</label><span>{d.deviceInfo.androidVersion}</span></div>}
            {d.platform && <div className="sp-field"><label>Plataforma</label><span>{d.platform}</span></div>}
            <div className="sp-field"><label>Último acceso</label><span>{formatDate(d.lastLogin || d.lastAccess)}</span></div>
          </div>
        ))}
      </div>
    );
  };

  const renderVersionsTab = () => {
    const v = userData?.versions;
    if (!v) return <div className="sp-empty">Sin datos de versión</div>;
    return (
      <div className="sp-versions">
        {v.currentVersion && (
          <div className="sp-version-card">
            <h4>Versión actual</h4>
            <div className="sp-field"><label>Versión</label><span>{v.currentVersion.versionName || '—'}</span></div>
            <div className="sp-field"><label>Último login</label><span>{formatDate(v.lastLogin)}</span></div>
            <div className="sp-field"><label>Plataforma</label><span>{v.platform || '—'}</span></div>
            <div className="sp-field"><label>Email</label><span>{v.userEmail || '—'}</span></div>
          </div>
        )}
        {v.web_session && (
          <div className="sp-version-card">
            <h4>Sesión Web</h4>
            <div className="sp-field"><label>Último acceso</label><span>{formatDate(v.web_session.lastAccess)}</span></div>
            <div className="sp-field"><label>Dispositivo</label><span>{v.web_session.deviceId || '—'}</span></div>
            <div className="sp-field"><label>Agente</label><span style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>{v.web_session.userAgent || '—'}</span></div>
          </div>
        )}
        {v.versionHistory?.length > 0 && (
          <div className="sp-version-card">
            <h4>Historial de versiones ({v.versionHistory.length})</h4>
            {v.versionHistory.slice(-5).reverse().map((vh, i) => (
              <div key={i} className="sp-field"><label>{formatDate(vh.lastUpdateCheck)}</label><span>v{vh.versionName}</span></div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderQuizzesTab = () => {
    const quizzes = userData?.quizzes;
    if (!quizzes || quizzes.length === 0) return <div className="sp-empty">Sin cuestionarios personales</div>;
    return (
      <div className="sp-quizzes">
        {quizzes.map(q => (
          <div key={q.id} className="sp-quiz-card">
            <div className="sp-quiz-title">{q.name || q.temaName || 'Sin título'}</div>
            <div className="sp-quiz-meta">
              <span>📝 {q.questionCount || '?'} preg.</span>
              <span>🎯 {q.difficulty || '—'}</span>
              <span>📅 {formatDate(q.createdAt)}</span>
            </div>
            {q.temaName && <div className="sp-quiz-theme">Tema: {q.temaName}</div>}
          </div>
        ))}
      </div>
    );
  };

  const renderRawTab = () => {
    if (!userData) return <div className="sp-empty">Sin datos</div>;
    const { uid, profile, versions, usersweb, devices, licencePremium, license, quizzes, ...rest } = userData;
    const raw = { uid, profile, versions, usersweb, devices, licencePremium, license, quizzes: quizzes ? { count: quizzes.length } : undefined, ...rest };
    return (
      <pre className="sp-raw-json">{JSON.stringify(raw, null, 2)}</pre>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Perfil': return renderProfileTab();
      case 'Licencias': return renderLicensesTab();
      case 'Dispositivos': return renderDevicesTab();
      case 'Versiones': return renderVersionsTab();
      case 'Quizzes': return renderQuizzesTab();
      case 'Raw': return renderRawTab();
      default: return null;
    }
  };

  return (
    <div className="sp-container">
      <style>{`
        .sp-container { background: #0a1628; border: 1px solid rgba(197,160,89,0.2); border-radius: 16px; padding: 20px; color: #e2e8f0; font-family: inherit; }
        .sp-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 10px; }
        .sp-header h2 { margin: 0; font-family: 'Orbitron', sans-serif; color: #c5a059; font-size: 1.2rem; display: flex; align-items: center; gap: 10px; }
        .sp-search-box { display: flex; gap: 8px; flex: 1; max-width: 500px; }
        .sp-search-box input { flex: 1; background: #060e1a; border: 1px solid #1e293b; border-radius: 8px; padding: 10px 14px; color: #fff; font-size: 0.9rem; outline: none; }
        .sp-search-box input:focus { border-color: #c5a059; }
        .sp-search-box button { background: #c5a059; color: #071322; border: none; border-radius: 8px; padding: 10px 20px; font-weight: bold; cursor: pointer; white-space: nowrap; }
        .sp-search-box button:disabled { opacity: 0.5; cursor: not-allowed; }
        .sp-error { background: rgba(239,68,68,0.15); border: 1px solid #ef4444; padding: 10px 14px; border-radius: 8px; color: #fca5a5; margin-bottom: 16px; font-size: 0.85rem; }
        .sp-user-list { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
        .sp-user-chip { background: rgba(197,160,89,0.1); border: 1px solid rgba(197,160,89,0.3); border-radius: 20px; padding: 6px 14px; cursor: pointer; font-size: 0.82rem; transition: all 0.2s; }
        .sp-user-chip:hover { background: rgba(197,160,89,0.25); }
        .sp-user-chip.active { background: #c5a059; color: #071322; font-weight: bold; border-color: #c5a059; }
        .sp-tabs { display: flex; gap: 4px; margin-bottom: 16px; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 8px; flex-wrap: wrap; }
        .sp-tab { background: transparent; border: none; color: #64748b; padding: 8px 16px; border-radius: 8px 8px 0 0; cursor: pointer; font-size: 0.85rem; font-weight: bold; transition: all 0.2s; }
        .sp-tab:hover { color: #cbd5e1; }
        .sp-tab.active { color: #c5a059; border-bottom: 2px solid #c5a059; }
        .sp-content { min-height: 300px; }
        .sp-empty { color: #64748b; text-align: center; padding: 40px; font-style: italic; }
        .sp-profile-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
        .sp-field { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; padding: 10px 14px; }
        .sp-field label { display: block; font-size: 0.65rem; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
        .sp-field span { color: #f1f5f9; font-size: 0.9rem; }
        .sp-licenses, .sp-devices, .sp-versions, .sp-quizzes { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; }
        .sp-license-card, .sp-device-card, .sp-version-card, .sp-quiz-card { background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 14px; }
        .sp-license-card h4, .sp-version-card h4 { margin: 0 0 8px; color: #c5a059; font-size: 0.85rem; }
        .sp-quiz-title { font-weight: bold; color: #f1f5f9; margin-bottom: 6px; font-size: 0.9rem; }
        .sp-quiz-meta { display: flex; gap: 12px; font-size: 0.75rem; color: #94a3b8; }
        .sp-quiz-theme { font-size: 0.75rem; color: #64748b; margin-top: 4px; }
        .sp-raw-json { background: #060e1a; border: 1px solid #1e293b; border-radius: 8px; padding: 16px; font-size: 0.72rem; overflow: auto; max-height: 500px; color: #94a3b8; white-space: pre-wrap; }
        .sp-loading { text-align: center; padding: 40px; color: #64748b; }
        .sp-shadow-actions { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; flex-wrap: wrap; }
        .sp-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 0.82rem; font-weight: bold; cursor: pointer; transition: all 0.2s; }
        .sp-btn-primary { background: #c5a059; color: #071322; }
        .sp-btn-primary:hover { background: #d4b06a; }
        .sp-btn-danger { background: #e94560; color: white; }
        .sp-btn-danger:hover { background: #c73a52; }
        .sp-shadow-hint { font-size: 0.75rem; color: #f59e0b; font-style: italic; }
        .sp-spinner { width: 24px; height: 24px; border: 3px solid rgba(197,160,89,0.2); border-top-color: #c5a059; border-radius: 50%; animation: spSpin 0.8s linear infinite; margin: 0 auto 12px; }
        @keyframes spSpin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="sp-header">
        <h2>🔍 Modo Shadow — Inspector de Usuario</h2>
        <div className="sp-search-box">
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Email, UID o nombre..."
          />
          <button onClick={searchUsers} disabled={searching || !searchTerm.trim()}>
            {searching ? '🔎' : 'Buscar'}
          </button>
        </div>
      </div>

      {error && <div className="sp-error">⚠️ {error}</div>}

      {foundUsers.length > 1 && (
        <div className="sp-user-list">
          {foundUsers.map(u => (
            <div
              key={u.uid}
              className={`sp-user-chip ${selectedUid === u.uid ? 'active' : ''}`}
              onClick={() => loadUserData(u.uid)}
            >
              👤 {u.email || u.uid} {u.displayName ? `(${u.displayName})` : ''}
            </div>
          ))}
        </div>
      )}

      {selectedUid && (
        <>
          <div className="sp-tabs">
            {TABS.map(tab => (
              <button key={tab} className={`sp-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                {tab}
              </button>
            ))}
          </div>
          <div className="sp-shadow-actions">
            {isShadowActive && shadowUid === selectedUid ? (
              <button className="sp-btn sp-btn-danger" onClick={() => { deactivateShadow(); navigate('/dashboard/admin'); }}>
                ✕ Salir del Modo Visor ({shadowUid.substring(0, 8)})
              </button>
            ) : (
              <button className="sp-btn sp-btn-primary" onClick={() => { activateShadow(selectedUid); navigate('/dashboard'); }}>
                🧑‍💻 Visualizar como usuario
              </button>
            )}
            {isShadowActive && shadowUid !== selectedUid && (
              <span className="sp-shadow-hint">Actualmente viendo a otro usuario. Cambia a este para visualizarlo.</span>
            )}
          </div>
          <div className="sp-content">
            {loadingData ? (
              <div className="sp-loading">
                <div className="sp-spinner" />
                <span>Cargando datos de {selectedUid.substring(0, 10)}...</span>
              </div>
            ) : renderTabContent()}
          </div>
        </>
      )}

      {!selectedUid && !error && (
        <div className="sp-empty" style={{ padding: '60px' }}>
          🔍 Ingresa un email, UID o nombre de usuario para inspeccionar sus datos.
        </div>
      )}
    </div>
  );
};

export default AdminShadowPanel;
